import { ShowMoreBtn } from 'components/BonusCard';
import { ScCMSTextBlock, ScContainer } from 'components/styles/common';
import { motion } from 'framer-motion/dist/framer-motion';
import { useMedia } from 'modules/common/hooks/useMedia';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useCurrentRoute } from 'react-navi';
import { FaqSection } from './FaqSection';
import type { IGetSeoContentData } from './helpers';
import { buildFaqSchema, getSeoContent } from './helpers';
import { ScSeoContent } from './styled';

const SeoContent = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const {
    url: { pathname },
  } = useCurrentRoute();

  const { isMobile } = useMedia();
  const [data, setData] = useState<IGetSeoContentData | undefined>();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    getSeoContent(language, pathname).then(setData);
  }, [language, pathname]);

  const isFaqItemsExist = !!data?.faqItems?.length;
  const isContentDataExist = !!data?.content;

  const faqSchema = useMemo(
    () =>
      isFaqItemsExist && data?.faqItems ? buildFaqSchema(data.faqItems) : null,
    [data?.faqItems, isFaqItemsExist]
  );

  if (!(data?.content || data?.faqItems)) return null;

  return (
    <ScContainer>
      {faqSchema && (
        <Helmet>
          <script type="application/ld+json">{faqSchema}</script>
        </Helmet>
      )}
      {isContentDataExist && (
        <ScSeoContent isOpen={isOpen}>
          <motion.div
            animate={isOpen ? 'open' : 'closed'}
            className="content"
            initial={{ maxHeight: isMobile ? 164 : 200 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            variants={{
              closed: { maxHeight: isMobile ? 164 : 200 },
              open: { maxHeight: 'none' },
            }}
          >
            {/** biome-ignore lint/security/noDangerouslySetInnerHtml: CMS content */}
            <ScCMSTextBlock
              dangerouslySetInnerHTML={{ __html: data.content }}
            />
          </motion.div>
          <div className="btn_wrapper">
            <ShowMoreBtn
              isListItem
              isOpen={isOpen}
              onClick={() => setIsOpen(!isOpen)}
            />
          </div>
        </ScSeoContent>
      )}
      {isFaqItemsExist && (
        <FaqSection faqItems={data.faqItems} faqTitle={data.faqTitle} />
      )}
    </ScContainer>
  );
};

export default SeoContent;
